import { AssetUrl, type AdFeatures, type ShopFeatures } from './common.types';
import { AdStatus } from '@/types';
import { AdOrderEnum } from '@/components/Shop/Shop.constants';

export enum GalleryItemType {
  Image = 'image',
  YouTube = 'youtube',
}

export interface ShopUser {
  id: string;
  legacyId: string;
}

export interface ShopCategory {
  id: string;
  name: string;
  icon: string;
  slug: string;
  path: string;
  count: number;
}

export enum AdKind {
  Bumped = 'BUMPED',
  Premium = 'PREMIUM',
  Vipped = 'VIPPED',
}

export interface AdNode {
  id: string;
  title: string;
  price: number;
  updatedAt: string;
  region: string;
  path: string;
  kinds: AdKind[];
  features?: Pick<AdFeatures, 'adFeatures'>;
  photo: AssetUrl;
  shop: {
    id: string;
    name: string;
    uri: string;
    workingDays: string;
    workingHours: string;
    logo: AssetUrl;
    phones?: { number: string }[];
    description: string;
  } | null;
  legacyResourceId: number;
  isBookmarked: boolean;
  status: AdStatus;
}

export type ShopScheduleDayOfWeek =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

export interface ShopSchedule {
  dayOfWeek: ShopScheduleDayOfWeek;
  openTime: string;
  closeTime: string;
  checked: boolean;
}

export type ShopDetailsFeatures = {
  shopFeatures?: ShopFeatures;
};

export interface ShopDetails {
  id: string;
  name: string;
  promo: string;
  address: string;
  phones: string[];
  isFavorited: boolean;
  favoriteShopsCount: number;
  features?: ShopDetailsFeatures;
  latitude: string;
  longitude: string;
  hits: number;
  adsCount: number;
  description: string;
  uri: string;
  workingDays: string;
  workingHours: string;
  useSchedule?: boolean;
  schedule?: ShopSchedule[];
  logo: AssetUrl;
  cover: AssetUrl;
  photos: AssetUrl[];
  videos: AssetUrl[];
  user: ShopUser;
  categories: ShopCategory[];
}

export enum ShopOrderEnum {
  Default = 'DEFAULT',
  ByAdsCount = 'BY_ADS_COUNT',
  ByNameAsc = 'BY_NAME_ASC',
  ByNameDesc = 'BY_NAME_DESC',
  ByPopularity = 'BY_POPULARITY',
}

export interface ShopAdsResponse {
  ShopAds: ShopAds;
}

export interface PageInfo {
  endCursor: string | null;
  hasNextPage: boolean;
}

export interface AdEdge {
  node: AdNode;
  cursor: string;
}

export interface ShopAds {
  edges: AdEdge[];
  pageInfo: {
    endCursor: string | null;
    hasNextPage: boolean;
  };
  totalCount: number;
}

export interface GalleryImage {
  type: GalleryItemType.Image;
  src: string;
  alt: string;
}

export interface GalleryYoutubeVideo {
  type: GalleryItemType.YouTube;
  videoId: string;
  alt: string;
}

export interface GalleryModalProps {
  items: GalleryItem[];
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  onClose: () => void;
  shopName: string;
  shopWorkTime?: string;
  workingDays?: string;
  phoneNumbers?: string[];
  getPhoneNumbers: () => void;
  isContactsOpen?: boolean;
  toggleContacts?: () => void;
}

export type GalleryItem = GalleryImage | GalleryYoutubeVideo;

export interface ShopGalleryPreviewProps {
  items: GalleryItem[];
  onOpenGallery: (index: number) => void;
}

export interface ShopAdsSectionProps {
  userLegacyId: string;
  onTotalCountChange?: (totalCount: number) => void;
}

export interface ShopViewProps {
  data?: ShopDetails;
  loading?: boolean;
  isSubscriptionEnabled?: boolean;
  isSelfOtherAdsEnabled?: boolean;
}

export interface SortBottomSheetProps {
  isOpen: boolean;
  close: () => void;
  selectedSort: AdOrderEnum;
  onSelect: (value: AdOrderEnum) => void;
  text: string;
}

export enum DayOfWeekEnum {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

export interface BuildScheduleState {
  scheduleType: 'general' | 'individual';
  generalSchedule: {
    roundTheClock?: boolean;
    days: DayOfWeekEnum[];
    openTime: string;
    closeTime: string;
  };
  individualSchedule: Record<
    DayOfWeekEnum,
    { checked: boolean; openTime: string; closeTime: string }
  >;
}

export interface ShopDayScheduleInput {
  dayOfWeek: DayOfWeekEnum;
  openTime: string;
  closeTime: string;
  checked: boolean;
}

export interface CreateShopAttributes {
  name: string;
  promo: string;
  description: string;
  categoriesIds: string[];
  logoUid?: string;
  coverUid?: string;
  email?: string;
  phones: string[];
  shortNumber?: string;
  schedule: ShopDayScheduleInput[];
}

export interface CreateShopResponse {
  createShop: {
    entity?: {
      id: string;
      name: string;
      uri: string;
    };
    errors?: Array<{
      code?: string;
      message: string;
      path?: string;
      extensions?: {
        code?: string;
      };
    }>;
  };
}

export type CreateFavoriteShopData = {
  createFavoriteShop: {
    entity: boolean;
    errors: Array<{ message: string; code?: string }>;
  };
};

export type CreateFavoriteShopVars = { shopId: string };

export type FavoriteShops = {
  id: string;
  logo: { url?: string };
  name: string;
  isFavorited: boolean;
  adsCount?: number;
  timeOnPlatform?: string;
  categories: ShopCategory[] | null;
  uri: string;
};

export interface ShopEdge {
  node: FavoriteShops;
  cursor: string;
}

export interface ShopConnection {
  edges: ShopEdge[];
  nodes: FavoriteShops[];
  pageInfo: PageInfo;
  totalCount: number;
}

export type FavoriteShopsData = {
  favoriteShops: ShopConnection;
};
